import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, X } from 'lucide-react';
import { summarizeCluster } from '@/lib/installmentDetector';

/**
 * Insight card that surfaces a detected installment-plan conversion in the
 * import preview. Region-neutral copy — no "EMI"-only jargon in the user
 * text, but examples span India/GCC/US/UK/AU.
 */
export function InstallmentPlanCard({ cluster, index, onApply, onDismiss }) {
  const s = summarizeCluster(cluster);
  const fmtAmt = (n) => {
    // Non-currency-locale-specific formatting. Uses the browser's default
    // number formatting so users see grouped thousands in their locale.
    try {
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
    } catch {
      return String(Math.round(n * 100) / 100);
    }
  };
  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const dt = d instanceof Date ? d : new Date(d);
      if (Number.isNaN(dt.getTime())) return String(d);
      return new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short' }).format(dt);
    } catch {
      return String(d);
    }
  };

  const scheduleParts = [];
  if (s.principalCount) scheduleParts.push(`${s.principalCount} principal payment${s.principalCount > 1 ? 's' : ''} (${fmtAmt(s.principalTotal)})`);
  if (s.interestCount) scheduleParts.push(`${s.interestCount} interest charge${s.interestCount > 1 ? 's' : ''} (${fmtAmt(s.interestTotal)})`);
  if (s.feeCount) scheduleParts.push(`${s.feeCount} fee${s.feeCount > 1 ? 's' : ''} (${fmtAmt(s.feeTotal)})`);
  if (s.taxCount) scheduleParts.push(`${s.taxCount} tax entr${s.taxCount > 1 ? 'ies' : 'y'} (${fmtAmt(s.taxTotal)})`);

  return (
    <Card
      className="border-primary/40 bg-primary/5"
      data-testid={`installment-plan-card-${index}`}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  <span>Installment plan detected</span>
                  <Badge variant="secondary" className="text-[10px] uppercase">
                    Suggested treatment
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Also known as EMI (India / GCC), Plan It (Amex), Flex Plan (Citi),
                  Pay Over Time (Chase), Affirm / Klarna / Clearpay / Afterpay.
                </p>
              </div>
              <button
                type="button"
                onClick={onDismiss}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                aria-label="Dismiss installment plan suggestion"
                data-testid={`installment-plan-dismiss-${index}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
              <div className="rounded-md border border-border/60 bg-background/60 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Purchase
                </div>
                <div className="mt-0.5 font-medium truncate" title={s.purchaseDesc}>
                  {s.purchaseDesc || '—'}
                </div>
                <div className="mt-0.5 text-muted-foreground">
                  {fmtAmt(s.purchaseAmount)} · {fmtDate(s.purchaseDate)}
                </div>
              </div>
              <div className="rounded-md border border-border/60 bg-background/60 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Bank's ledger reversal
                </div>
                <div className="mt-0.5 font-medium truncate" title={s.reversalDesc}>
                  {s.reversalDesc || '—'}
                </div>
                <div className="mt-0.5 text-muted-foreground">
                  +{fmtAmt(s.purchaseAmount)} · {fmtDate(s.reversalDate)}
                </div>
              </div>
            </div>

            {scheduleParts.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                Also in this batch: {scheduleParts.join(' · ')}
              </div>
            )}

            <div className="mt-3 rounded-md bg-background/70 px-3 py-2 text-xs text-muted-foreground">
              <div className="font-medium text-foreground">If you apply the preset:</div>
              <ul className="mt-1 space-y-0.5">
                <li>• <span className="text-foreground">Purchase</span> stays as an expense with your category</li>
                <li>• <span className="text-foreground">Reversal</span> becomes a Transfer (cancels the phantom &ldquo;income&rdquo;)</li>
                <li>• <span className="text-foreground">Principal</span> payments become Transfers (paying down the plan, not new spending)</li>
                <li>• <span className="text-foreground">Interest / fees / taxes</span> stay as expenses under Fees &amp; Charges</li>
              </ul>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={onDismiss}
                data-testid={`installment-plan-dismiss-btn-${index}`}
              >
                Not an installment plan
              </Button>
              <Button
                size="sm"
                onClick={onApply}
                data-testid={`installment-plan-apply-${index}`}
              >
                Apply preset
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
